import React from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, MixerHorizontalIcon, StarFilledIcon, StarIcon } from '@radix-ui/react-icons';
import { Text, TextInput, UnstyledButton } from '@/shared/ui';

interface SearchRowProps {
  searchPlaceholder: string;
  searchValue: string;
  onSearchInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFiltersToggle: () => void;
  filterCount: number;
  hideFilters?: boolean;
  /** Whether the list is currently narrowed to the caller's own favourites. */
  favoritesOnly?: boolean;
  /** Omitted hides the star entirely, for lists that have no favourites. */
  onToggleFavorites?: () => void;
  /**
   * Optional action rendered after the filters toggle — for page-level
   * actions that read the list rather than create in it, such as opening
   * the settings that govern it.
   */
  trailingAction?: React.ReactNode;
}

/**
 * Renders the search input and the filters toggle button with an active-count badge.
 */
export const PageHeaderSearchRow: React.FC<SearchRowProps> = ({ searchPlaceholder, searchValue, onSearchInputChange, onFiltersToggle, filterCount, hideFilters, favoritesOnly = false, onToggleFavorites, trailingAction }) => {
  const { t } = useTranslation('features/properties');
  return (
  // flexWrap keeps every control inside the card on narrow/mobile widths:
  // without it the row is one non-wrapping line and the trailing "Agrupar"
  // group-by control spills past the card's right boundary. A real minimum
  // width on the search field stops it collapsing toward zero (which left
  // only its absolutely-positioned magnifier showing, flush against the
  // favourites star, so the two read as overlapping).
  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
    <div data-tour="page-header-search" style={{ position: 'relative', flex: '1 1 12rem', minWidth: '12rem' }}>
      <MagnifyingGlassIcon
        width={14}
        height={14}
        style={{
          position: 'absolute',
          left: 12,
          top: 15,
          color: 'var(--text-tertiary)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <TextInput
        type="text"
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={(e) => onSearchInputChange(e as React.ChangeEvent<HTMLInputElement>)}
        className="[&_input]:pl-(--space-8) [&_input]:bg-(--surface-card)"
      />
    </div>
    {onToggleFavorites && (
      // Its own control rather than a row inside the filter panel: a
      // shortlist is something a broker flips on and off while scanning,
      // and two clicks into a panel is not that.
      <UnstyledButton
        type="button"
        data-testid="favorites-filter-button"
        onClick={onToggleFavorites}
        aria-pressed={favoritesOnly}
        aria-label={t('listHeader.favorites')}
        title={t('listHeader.favorites')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 38,
          height: 38,
          flex: 'none',
          background: favoritesOnly ? 'var(--selection-active)' : 'transparent',
          color: favoritesOnly ? 'var(--seed-base)' : 'var(--text-secondary)',
          border: favoritesOnly ? 'none' : '1px solid var(--border-medium)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
        }}
      >
        {favoritesOnly ? <StarFilledIcon width={16} height={16} /> : <StarIcon width={16} height={16} />}
      </UnstyledButton>
    )}
    {!hideFilters && (
    <UnstyledButton
      type="button"
      data-testid="filters-button"
      onClick={onFiltersToggle}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        fontSize: 'var(--text-body-sm)',
        fontWeight: 'var(--font-medium)' as unknown as number,
        background: filterCount > 0 ? 'var(--selection-active)' : 'transparent',
        color: filterCount > 0 ? 'var(--seed-base)' : 'var(--text-secondary)',
        border: filterCount > 0 ? 'none' : '1px solid var(--border-medium)',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        fontFamily: 'var(--font-family)',
        position: 'relative',
        flex: 'none',
      }}
    >
      <MixerHorizontalIcon width={14} height={14} />
      {t('listHeader.filters')}
      {filterCount > 0 && (
        <Text
          as="span"
          data-testid="filter-count-badge"
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: 'var(--seed-base)',
            color: 'var(--button-primary-text)',
            fontSize: 'var(--text-micro)',
            fontWeight: 'var(--font-bold)' as unknown as number,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {filterCount}
        </Text>
      )}
    </UnstyledButton>
    )}
    {trailingAction}
  </div>
  );
};
